import {
    type DimensionValue,
    type TextStyle,
    type ViewStyle,
    StyleSheet,
    Platform
} from "react-native";
import type {
    MarkdownQuoteIconContainerStyleConstantType,
    MarkdownReadingInlineStyleConstantType,
    MarkdownInlineCodeStyleConstantType,
    MarkdownDecorationStyleConstantType,
    MarkdownLegacyDimensionConstantType,
    MarkdownQuoteIconSizeConstantType,
    MarkdownHeadingStyleConstantType,
    MarkdownStrongStyleConstantType,
    MarkdownSpacerStyleConstantType,
    MarkdownListMarkerConstantType,
    MarkdownImageSizeConstantType,
    MarkdownVariantConstantType,
    MarkdownDynamicStyles,
    MarkdownImageSizeType,
    MarkdownKeys
} from "./type";
import {
    MARKDOWN_READING_REGULAR_WEIGHT,
    MARKDOWN_READING_STRONG_WEIGHT,
    MARKDOWN_FALLBACK_VARIANT,
    MARKDOWN_VARIANT_ALIASES,
    MARKDOWN_STRONG_VARIANT,
    MARKDOWN_LIST_MARKERS,
    MARKDOWN_CODE_VARIANT
} from "./constant";
import {
    getMarkdownDefaultVariant
} from "./util";
import type {
    Mutable
} from "../../types";
import {
    getAndroidFontFamily,
    getAndroidFontWeight,
    webStyle
} from "../../utils";
import {
    getCodeFontFamily
} from "../codeViewer/stylesheet";
import {
    getCornerStyle
} from "../../helpers/theme/corner";

export const getMarkdownVariant = ({
    nativeType,
    variants
}: MarkdownVariantConstantType): keyof NCoreUIKit.Typography => {
    const key = nativeType as MarkdownKeys;

    const aliasKey = MARKDOWN_VARIANT_ALIASES[key];

    const aliasVariant = aliasKey ? variants?.[aliasKey] : undefined;

    return variants?.[key] ?? aliasVariant ?? getMarkdownDefaultVariant(nativeType);
};

export const getMarkdownListMarker = ({
    node
}: MarkdownListMarkerConstantType): string => {
    if(node.isOrdered) {
        return `${node.order ?? 1}.`;
    }

    const markerIndex = Math.min(node.level ?? 0, MARKDOWN_LIST_MARKERS.length - 1);

    return MARKDOWN_LIST_MARKERS[markerIndex] ?? "";
};

export const getMarkdownStrongStyle = ({
    typography
}: MarkdownStrongStyleConstantType): TextStyle => {
    const strongTypography = typography[MARKDOWN_STRONG_VARIANT];

    return {
        fontWeight: strongTypography.fontWeight as TextStyle["fontWeight"],
        fontFamily: strongTypography.fontFamily
    };
};

export const getMarkdownReadingInlineStyle = ({
    isEmphasis,
    isArabic,
    isStrong,
    token
}: MarkdownReadingInlineStyleConstantType): TextStyle => {
    const isBold = isStrong || isEmphasis && isArabic;

    const isItalic = isEmphasis && !isArabic;

    if(Platform.OS !== "android") {
        return {
            fontWeight: isBold ? String(MARKDOWN_READING_STRONG_WEIGHT) as TextStyle["fontWeight"] : undefined,
            fontStyle: isItalic ? "italic" : undefined
        };
    }

    const baseFontFamily = token.fontFamily.split("-")[0] ?? token.fontFamily;

    const fontWeight = isBold ? MARKDOWN_READING_STRONG_WEIGHT : getAndroidFontWeight({
        fontFamily: token.fontFamily
    }) ?? MARKDOWN_READING_REGULAR_WEIGHT;

    const uprightFontFamily = getAndroidFontFamily({
        fontFamily: baseFontFamily,
        fontWeight
    });

    const fontFamily = getAndroidFontFamily({
        fontStyle: isItalic ? "italic" : "normal",
        fontFamily: baseFontFamily,
        fontWeight
    });

    return {
        fontStyle: isItalic && fontFamily === uprightFontFamily ? "italic" : "normal",
        fontWeight: "normal",
        fontFamily
    };
};

export const getMarkdownInlineCodeStyle = ({
    typography,
    fontSize,
    radiuses,
    variant,
    colors,
    spaces
}: MarkdownInlineCodeStyleConstantType): TextStyle => {
    const codeTypography = typography[variant];

    const bodyTypography = typography[MARKDOWN_FALLBACK_VARIANT];

    return {
        fontSize: fontSize * codeTypography.fontSize / bodyTypography.fontSize,
        fontFamily: variant === MARKDOWN_CODE_VARIANT ? getCodeFontFamily({
            typography
        }) : codeTypography.fontFamily,
        backgroundColor: colors.content.container.subtle,
        ...webStyle({
            paddingHorizontal: spaces.spacingXs,
            paddingVertical: spaces.spacingXxs,
            borderRadius: radiuses.xs
        })
    };
};

export const getMarkdownDecorationStyle = ({
    isStrikethrough,
    isUnderline
}: MarkdownDecorationStyleConstantType): TextStyle | undefined => {
    if(isUnderline && isStrikethrough) {
        return stylesheet.underlineLineThrough;
    }

    if(isUnderline) {
        return stylesheet.underline;
    }

    if(isStrikethrough) {
        return stylesheet.lineThrough;
    }

    return undefined;
};

export const getMarkdownHeadingStyle = ({
    isHeading,
    isFirst,
    spaces
}: MarkdownHeadingStyleConstantType): TextStyle | undefined => {
    if(!isHeading || isFirst) {
        return undefined;
    }

    return {
        marginTop: spaces.spacingSm
    };
};

export const getMarkdownSpacerStyle = ({
    typography,
    variant
}: MarkdownSpacerStyleConstantType): ViewStyle => {
    return {
        height: typography[variant].fontSize
    };
};

export const getMarkdownQuoteIconSize = ({
    blockquoteIconSize,
    typography,
    iconSizes
}: MarkdownQuoteIconSizeConstantType): number => {
    if(blockquoteIconSize) {
        return typography[blockquoteIconSize].fontSize;
    }

    return iconSizes.medium;
};

export const getMarkdownQuoteIconContainerStyle = ({
    typography,
    iconSize,
    variant
}: MarkdownQuoteIconContainerStyleConstantType): ViewStyle => {
    return {
        marginTop: Math.max((typography[variant].lineHeight - iconSize) / 2, 0)
    };
};

export const getMarkdownLegacyDimension = ({
    availableWidth,
    value
}: MarkdownLegacyDimensionConstantType): DimensionValue | undefined => {
    if(value === undefined || typeof value === "number" || value.indexOf("%") === -1 || !availableWidth) {
        return value as DimensionValue | undefined;
    }

    return (availableWidth * Number(value.split("%")[0])) / 100;
};

export const getMarkdownImageSize = ({
    availableWidth,
    naturalSize,
    size
}: MarkdownImageSizeConstantType): MarkdownImageSizeType | null => {
    if(size) {
        return {
            height: getMarkdownLegacyDimension({
                value: size[1],
                availableWidth
            }),
            width: getMarkdownLegacyDimension({
                value: size[0],
                availableWidth
            })
        };
    }

    if(!naturalSize || !naturalSize.width || !naturalSize.height) {
        return null;
    }

    const width = availableWidth ? Math.min(naturalSize.width, availableWidth) : naturalSize.width;

    return {
        height: (width * naturalSize.height) / naturalSize.width,
        width
    };
};

const stylesheet = StyleSheet.create({
    underlineLineThrough: {
        textDecorationLine: "underline line-through"
    },
    lineThrough: {
        textDecorationLine: "line-through"
    },
    link: {
        ...webStyle({
            textUnderlineOffset: "0.2em"
        })
    },
    underline: {
        textDecorationLine: "underline"
    },
    leftContainer: {
        justifyContent: "flex-start",
        alignItems: "flex-start",
        alignSelf: "flex-start"
    },
    rightContainer: {
        justifyContent: "flex-end",
        alignItems: "flex-end",
        alignSelf: "flex-end"
    },
    linkBlock: {
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    centerContainer: {
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "center"
    },
    blockquoteContainer: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    listItem: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    listMarker: {
        alignItems: "flex-end",
        flexShrink: 0
    },
    blockquoteIconContainer: {
        flexShrink: 0
    },
    figure: {
        alignItems: "center",
        alignSelf: "stretch"
    },
    list: {
        alignSelf: "stretch"
    },
    imageCaption: {
        textAlign: "center"
    },
    italic: {
        fontStyle: "italic"
    },
    listMarkerText: {
        fontVariant: [
            "tabular-nums"
        ]
    },
    blockquoteContent: {
        minWidth: 0,
        flex: 1
    },
    image: {
        maxWidth: "100%"
    },
    imagePending: {
        width: "100%",
        height: 0
    },
    enter: {
        flexShrink: 1,
        width: "100%"
    },
    blockquoteText: {
        flexShrink: 1
    },
    blockquoteIcon: {
        flexShrink: 0
    },
    listContent: {
        minWidth: 0,
        flex: 1
    },
    container: {
    }
});

export const useStyles = ({
    shapeConfigs,
    typography,
    isReading,
    radiuses,
    colors,
    spaces,
    syntax
}: MarkdownDynamicStyles) => {
    const styles = {
        blockquoteContainer: {
            backgroundColor: colors.content.container.mid,
            ...getCornerStyle({
                radius: radiuses.form,
                shapeConfigs
            }),
            padding: spaces.spacingMd,
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        listMarker: {
            paddingEnd: spaces.spacingXs,
            minWidth: spaces.spacingLg
        } as Mutable<ViewStyle>,
        strong: getMarkdownStrongStyle({
            typography
        }) as Mutable<TextStyle>,
        blockquoteContent: {
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>,
        listContent: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        list: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        container: {
        } as Mutable<ViewStyle>
    };

    if(syntax === "standard") {
        styles.container.gap = isReading ? spaces.spacingLg : spaces.spacingMd;
    }

    if(isReading) {
        styles.container.maxWidth = shapeConfigs.readingMaxWidth;
    }

    return styles;
};

export default stylesheet;
